import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Play, Pause, ChevronLeft, ChevronRight, Volume2, VolumeX,
  Type, Sun, Moon, Contrast, Lock, ShoppingBag, Mail, Download,
  Highlighter, Trash2, ArrowRight, ListTree, BookOpen, Award,
  Feather, Users, Sparkles, Compass, ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchReaderToc, fetchReaderChapter, requestReaderUnlock, verifyReaderToken,
} from "@/lib/api";
import { StreakCalendar, markVisitToday } from "@/components/StreakCalendar";
import { ReaderCompanion } from "@/components/ReaderCompanion";
import "@/reader.css";

const TOKEN_KEY = "lg_reader_token";
const SETTINGS_KEY = "lg_reader_settings";
const REFLECTIONS_KEY = "lg_reader_reflections";
const HIGHLIGHTS_KEY = "lg_reader_highlights";

const DEFAULT_SETTINGS = { fontSize: 17, lineHeight: 1.85, theme: "light" };

const splitSentences = (text) =>
  text.split(/(?<=[.!?"'”’])\s+(?=[A-Z“"'‘])/g).filter((s) => s.trim().length > 0);

const readStore = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};
const writeStore = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
};

const Reader = () => {
  const [params, setParams] = useSearchParams();
  const [toc, setToc] = useState([]);
  const [chapterId, setChapterId] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);

  // Settings (font size / theme)
  const [settings, setSettings] = useState(() => ({ ...DEFAULT_SETTINGS, ...readStore(SETTINGS_KEY, {}) }));
  useEffect(() => writeStore(SETTINGS_KEY, settings), [settings]);

  // Unlock token
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState("");
  const [sending, setSending] = useState(false);

  // TTS
  const [isPlaying, setIsPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [activeSentence, setActiveSentence] = useState({ pi: -1, si: -1 });
  const utterRef = useRef(null);
  const cancelRef = useRef(null);

  // Reflection + highlights (per chapter, localStorage)
  const [reflection, setReflection] = useState("");
  const [highlights, setHighlights] = useState([]);
  const contentRef = useRef(null);

  // ---- Boot: load token from URL, TOC, then default chapter ----
  useEffect(() => {
    const urlTok = params.get("token");
    if (urlTok) {
      (async () => {
        try {
          const res = await verifyReaderToken(urlTok);
          if (res.ok) {
            localStorage.setItem(TOKEN_KEY, urlTok);
            setToken(urlTok);
            setVerifiedEmail(res.email || "");
            toast.success("Reader unlocked — the full book is now available on this device.");
            const next = new URLSearchParams(params);
            next.delete("token");
            setParams(next, { replace: true });
          } else {
            toast.error("That unlock link is invalid or has expired.");
          }
        } catch { /* ignore */ }
      })();
    } else if (token) {
      verifyReaderToken(token)
        .then((r) => r.ok && setVerifiedEmail(r.email || ""))
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchReaderToc().then((t) => {
      setToc(t);
      const initial = params.get("ch") || t[0]?.id;
      if (initial) setChapterId(initial);
    }).catch(() => toast.error("Could not load the book."));
    markVisitToday();
  }, []); // eslint-disable-line

  // ---- Load chapter ----
  useEffect(() => {
    if (!chapterId) return;
    setLoading(true);
    stopTTS();
    setActiveSentence({ pi: -1, si: -1 });
    fetchReaderChapter(chapterId, token || undefined)
      .then((res) => {
        setChapter(res.chapter);
        setLocked(!!res.locked);
        // Load per-chapter reflection + highlights
        const refl = readStore(REFLECTIONS_KEY, {});
        setReflection(refl[chapterId] || "");
        const hls = readStore(HIGHLIGHTS_KEY, {});
        setHighlights(hls[chapterId] || []);
        // Update URL param
        const next = new URLSearchParams(params);
        next.set("ch", chapterId);
        setParams(next, { replace: true });
        window.scrollTo({ top: 0, behavior: "smooth" });
      })
      .catch(() => toast.error("Chapter unavailable."))
      .finally(() => setLoading(false));
  }, [chapterId, token]); // eslint-disable-line

  // Persist reflection (debounced)
  useEffect(() => {
    if (!chapterId) return;
    const id = setTimeout(() => {
      const all = readStore(REFLECTIONS_KEY, {});
      if (reflection.trim()) all[chapterId] = reflection;
      else delete all[chapterId];
      writeStore(REFLECTIONS_KEY, all);
    }, 500);
    return () => clearTimeout(id);
  }, [reflection, chapterId]);

  // ---- Highlight handling ----
  const addHighlight = useCallback(() => {
    const sel = window.getSelection?.();
    if (!sel || sel.isCollapsed) return;
    const text = sel.toString().trim();
    if (text.length < 3 || text.length > 400) return;
    // Only accept selections within the content area
    const anchor = sel.anchorNode?.parentElement?.closest?.("[data-reader-para]");
    if (!anchor || !contentRef.current?.contains(anchor)) return;
    setHighlights((prev) => {
      if (prev.some((h) => h.text === text)) return prev;
      const next = [{ text, at: Date.now() }, ...prev].slice(0, 100);
      const all = readStore(HIGHLIGHTS_KEY, {});
      all[chapterId] = next;
      writeStore(HIGHLIGHTS_KEY, all);
      return next;
    });
    sel.removeAllRanges();
    toast.success("Highlight saved");
  }, [chapterId]);

  const removeHighlight = (text) => {
    setHighlights((prev) => {
      const next = prev.filter((h) => h.text !== text);
      const all = readStore(HIGHLIGHTS_KEY, {});
      all[chapterId] = next;
      writeStore(HIGHLIGHTS_KEY, all);
      return next;
    });
  };

  const exportHighlights = () => {
    const lines = highlights.map((h) => `• ${h.text}`).join("\n\n");
    const blob = new Blob(
      [`Highlights from ${chapter?.label || "The Love Genome"}\n\n${lines}`],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `love-genome-highlights-${chapterId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- TTS ----
  const flatSentences = useMemo(() => {
    if (!chapter) return [];
    return chapter.paragraphs.flatMap((p, pi) =>
      splitSentences(p).map((s, si) => ({ text: s, pi, si })));
  }, [chapter]);  const stopTTS = () => {
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    utterRef.current = null;
    setIsPlaying(false);
    cancelRef.current = true;
  };

  const speakFrom = (startIndex) => {
    if (!("speechSynthesis" in window)) {
      toast.error("Your browser doesn't support read-aloud.");
      return;
    }
    window.speechSynthesis.cancel();
    cancelRef.current = false;
    setIsPlaying(true);
    let i = Math.max(0, startIndex);

    const speakNext = () => {
      if (cancelRef.current || i >= flatSentences.length) {
        setIsPlaying(false);
        setActiveSentence({ pi: -1, si: -1 });
        return;
      }
      const cur = flatSentences[i];
      setActiveSentence({ pi: cur.pi, si: cur.si });
      const u = new SpeechSynthesisUtterance(cur.text);
      u.rate = Math.min(4, Math.max(0.5, rate));
      u.pitch = 1;
      u.onend = () => { i += 1; speakNext(); };
      u.onerror = () => { i += 1; speakNext(); };
      utterRef.current = u;
      window.speechSynthesis.speak(u);
    };
    speakNext();
  };

  const togglePlay = () => {
    if (isPlaying) stopTTS();
    else speakFrom(0);
  };

  useEffect(() => () => stopTTS(), []);
  useEffect(() => {
    // When rate changes mid-play, restart from current sentence
    if (isPlaying) {
      const idx = flatSentences.findIndex(
        (s) => s.pi === activeSentence.pi && s.si === activeSentence.si,
      );
      speakFrom(Math.max(0, idx));
    }
  }, [rate]); // eslint-disable-line

  // ---- Unlock request ----
  const requestUnlock = async (e) => {
    e.preventDefault();
    if (!unlockEmail) return;
    setSending(true);
    try {
      await requestReaderUnlock(unlockEmail);
      toast.success("If we find a purchase, an unlock link is on its way.");
      setUnlockOpen(false);
      setUnlockEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const chIndex = toc.findIndex((c) => c.id === chapterId);
  const prev = chIndex > 0 ? toc[chIndex - 1] : null;
  const next = chIndex >= 0 && chIndex < toc.length - 1 ? toc[chIndex + 1] : null;

  const progressPct = toc.length
    ? Math.round(((chIndex + 1) / toc.length) * 100)
    : 0;

  const renderParagraph = (text, pi) => {
    // Split into sentences for TTS highlighting
    const sents = splitSentences(text);
    // Apply manual highlights: wrap first occurrence of each snippet in <mark>
    return (
      <p
        data-reader-para={pi}
        key={pi}
        style={{
          fontSize: `${settings.fontSize}px`,
          lineHeight: settings.lineHeight,
          marginBottom: "1.35em",
        }}
        className="font-editorial"
      >
        {sents.map((s, si) => {
          const isActive = activeSentence.pi === pi && activeSentence.si === si;
          // Apply highlights within this sentence
          let content = s;
          const parts = [];
          if (highlights.length) {
            let remaining = content;
            let key = 0;
            while (remaining.length) {
              const hit = highlights
                .map((h) => ({ h, idx: remaining.toLowerCase().indexOf(h.text.toLowerCase()) }))
                .filter((x) => x.idx >= 0)
                .sort((a, b) => a.idx - b.idx)[0];
              if (!hit) { parts.push(<React.Fragment key={key++}>{remaining}</React.Fragment>); break; }
              if (hit.idx > 0) parts.push(<React.Fragment key={key++}>{remaining.slice(0, hit.idx)}</React.Fragment>);
              const matchLen = hit.h.text.length;
              parts.push(<mark key={key++} className="lg-highlight">{remaining.slice(hit.idx, hit.idx + matchLen)}</mark>);
              remaining = remaining.slice(hit.idx + matchLen);
            }
          } else {
            parts.push(<React.Fragment key="only">{content}</React.Fragment>);
          }
          return (
            <span
              key={si}
              className={isActive ? "sent-active" : ""}
              onClick={() => {
                const idx = flatSentences.findIndex((x) => x.pi === pi && x.si === si);
                if (idx >= 0) { stopTTS(); setTimeout(() => speakFrom(idx), 60); }
              }}
              style={{ transition: "background-color 0.3s ease" }}
            >
              {parts}{" "}
            </span>
          );
        })}
      </p>
    );
  };

  const themeCycle = () => {
    const order = ["light", "dark", "high-contrast"];
    const next = order[(order.indexOf(settings.theme) + 1) % order.length];
    setSettings((s) => ({ ...s, theme: next }));
  };

  const changeFont = (delta) => {
    setSettings((s) => ({ ...s, fontSize: Math.min(24, Math.max(14, s.fontSize + delta)) }));
  };

  // Metadata for the color-coded interactive blocks (labels + icons)
  const BLOCK_META = {
    authors_life: { label: "From the Author's Life", Icon: Feather },
    real_life:    { label: "Love in Real Life",       Icon: Users },
    genome_pause: { label: "Genome Pause",             Icon: Compass },
    try_today:    { label: "Try This Today",           Icon: Sparkles },
    summary:      { label: "Chapter Summary",          Icon: ScrollText },
  };

  // Track the running paragraph-index so highlighter + TTS stay in sync
  // with the flatSentences array (which is derived from chapter.paragraphs).
  const renderChapterBody = () => {
    if (!chapter) return null;
    // Legacy chapters without block metadata: fall back to plain paragraphs
    if (!Array.isArray(chapter.blocks) || chapter.blocks.length === 0) {
      return chapter.paragraphs.map(renderParagraph);
    }

    let paraCursor = 0;
    const nodes = [];
    chapter.blocks.forEach((b, bi) => {
      if (b.type === "body") {
        nodes.push(renderParagraph(b.text, paraCursor));
        paraCursor += 1;
      } else if (b.type === "heading") {
        nodes.push(
          <h3 className="reader-subhead font-display" key={`h-${bi}`}
              data-testid={`reader-subhead-${bi}`}>
            {b.text}
          </h3>,
        );
        paraCursor += 1;
      } else if (b.type === "bullet") {
        nodes.push(
          <ul className="reader-bullets" key={`ul-${bi}`}>
            {b.items.map((it, ii) => {
              const idx = paraCursor + ii;
              return <li key={ii} data-reader-para={idx}
                         style={{ fontSize: `${settings.fontSize}px`, lineHeight: settings.lineHeight }}>{it}</li>;
            })}
          </ul>,
        );
        paraCursor += b.items.length;
      } else if (b.type === "numbered") {
        nodes.push(
          <div className="reader-pillar" key={`p-${bi}`}>
            <div className="reader-pillar__n">{b.index}</div>
            <div className="reader-pillar__text font-editorial"
                 data-reader-para={paraCursor}
                 style={{ fontSize: `${settings.fontSize}px`, lineHeight: settings.lineHeight }}>
              {b.text}
            </div>
          </div>,
        );
        paraCursor += 1;
      } else if (BLOCK_META[b.type]) {
        const { label, Icon } = BLOCK_META[b.type];
        nodes.push(
          <aside className={`reader-block reader-block--${b.type}`} key={`b-${bi}`}
                 data-testid={`reader-block-${b.type}`}>
            <span className="reader-block__label">
              <Icon className="icon" /> {label}
            </span>
            {b.paragraphs.map((p, pi) => (
              <p key={pi} data-reader-para={paraCursor + pi}
                 style={{ fontSize: `${settings.fontSize - 1}px`, lineHeight: settings.lineHeight }}>
                {p}
              </p>
            ))}
          </aside>,
        );
        paraCursor += b.paragraphs.length;
      }
    });
    return nodes;
  };

  return (
    <div className="reader-body min-h-screen pt-24 pb-24" data-theme={settings.theme} data-testid="reader-page">
      {/* Toolbar */}
      <div className="fixed top-20 left-0 right-0 z-30 border-b"
           style={{ background: "var(--reader-surface)", borderColor: "var(--reader-line)" }}>
        <div className="max-w-[1400px] mx-auto px-3 md:px-6 h-14 flex items-center gap-2 md:gap-4">
          <button
            onClick={() => setTocOpen((v) => !v)}
            aria-label="Chapters"
            data-testid="reader-toc-toggle"
            className="p-2 hover:opacity-80"
          >
            <ListTree className="w-5 h-5" style={{ color: "var(--reader-accent)" }} />
          </button>
          <div className="flex-1 min-w-0 hidden md:block">
            <p className="font-body text-xs opacity-60 truncate">{chapter?.part}</p>
            <p className="font-display text-sm truncate" data-testid="reader-current-chapter">
              {chapter?.label} {chapter?.title ? `— ${chapter.title}` : ""}
            </p>
          </div>

          {/* TTS controls */}
          <div className="flex items-center gap-1 border rounded-none px-1"
               style={{ borderColor: "var(--reader-line)" }}>
            <button onClick={togglePlay} data-testid="reader-play-toggle"
                    className="p-2" title={isPlaying ? "Pause" : "Read aloud"}>
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <div className="flex items-center gap-0.5 text-[10px] font-body uppercase tracking-wider">
              {[1, 2, 4].map((r) => (
                <button
                  key={r}
                  onClick={() => setRate(r)}
                  data-testid={`reader-rate-${r}`}
                  className={`px-1.5 py-0.5 ${rate === r ? "font-bold" : "opacity-60"}`}
                  style={{ color: rate === r ? "var(--reader-accent)" : "inherit" }}
                >
                  {r}x
                </button>
              ))}
            </div>
          </div>

          {/* Font + theme */}
          <div className="hidden sm:flex items-center gap-1 border px-1"
               style={{ borderColor: "var(--reader-line)" }}>
            <button onClick={() => changeFont(-1)} data-testid="reader-font-dec" className="p-2" title="Smaller"><Type className="w-3 h-3" /></button>
            <span className="text-[10px] font-body opacity-60 w-6 text-center tabular-nums">{settings.fontSize}</span>
            <button onClick={() => changeFont(1)} data-testid="reader-font-inc" className="p-2" title="Larger"><Type className="w-5 h-5" /></button>
          </div>

          <button onClick={themeCycle} data-testid="reader-theme-toggle"
                  className="p-2 hover:opacity-80" title={`Theme: ${settings.theme}`}>
            {settings.theme === "light" ? <Moon className="w-4 h-4" /> :
             settings.theme === "dark" ? <Contrast className="w-4 h-4" /> :
             <Sun className="w-4 h-4" />}
          </button>

          <Link to="/dictionary" className="hidden md:inline-flex items-center gap-1.5 font-body text-[11px] uppercase tracking-widest opacity-70 hover:opacity-100" data-testid="reader-dictionary-link">
            <BookOpen className="w-3.5 h-3.5" /> Dictionary
          </Link>
          <Link to="/store" className="hidden md:inline-flex items-center gap-2 bg-gold text-ink px-3 py-1.5 font-body text-xs" data-testid="reader-get-book-top">
            <ShoppingBag className="w-3 h-3" /> Get the Book
          </Link>
        </div>

        {/* Progress bar */}
        <div className="h-1" style={{ background: "var(--reader-line)" }}>
          <div className="h-1" style={{ width: `${progressPct}%`, background: "var(--reader-accent)", transition: "width 0.4s ease" }} />
        </div>
      </div>

      {/* TOC drawer */}
      {tocOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setTocOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute left-0 top-0 h-full w-full sm:w-96 border-r overflow-y-auto pt-24 pb-10"
            style={{ background: "var(--reader-bg)", color: "var(--reader-ink)", borderColor: "var(--reader-line)" }}
            data-testid="reader-toc-panel"
          >
            <div className="px-6">
              <p className="eyebrow mb-4" style={{ color: "var(--reader-accent)" }}>Contents</p>
              <div className="space-y-1">
                {toc.map((c) => {
                  const active = c.id === chapterId;
                  return (
                    <button
                      key={c.id}
                      onClick={() => { setChapterId(c.id); setTocOpen(false); }}
                      data-testid={`reader-toc-${c.id}`}
                      className="w-full text-left p-3 flex items-center gap-3 border-l-2"
                      style={{
                        borderColor: active ? "var(--reader-accent)" : "transparent",
                        background: active ? "var(--reader-surface)" : "transparent",
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-[10px] uppercase tracking-widest opacity-50">{c.part || c.label}</p>
                        <p className="font-display text-sm truncate">
                          {c.title || c.label}
                        </p>
                      </div>
                      {!c.free && <Lock className="w-3.5 h-3.5 opacity-60" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Content */}
      <div className="max-w-[760px] mx-auto px-5 md:px-8 pt-24">
        {loading && (
          <p className="font-editorial italic text-2xl opacity-60 text-center py-16">Loading…</p>
        )}
        {!loading && chapter && (
          <article ref={contentRef} onMouseUp={addHighlight} onTouchEnd={addHighlight}>
            <p className="eyebrow mb-4" style={{ color: "var(--reader-accent)" }}>
              {chapter.part || chapter.label}
            </p>
            <h1 className="font-display leading-tight mb-6"
                style={{ fontSize: "clamp(2rem,4vw,3.25rem)" }}
                data-testid="reader-chapter-title">
              {chapter.title || chapter.label}
            </h1>

            {chapter.epigraph && (
              <p className="reader-epigraph" data-testid="reader-chapter-epigraph">
                &ldquo;{chapter.epigraph}&rdquo;
              </p>
            )}

            {renderChapterBody()}

            {locked && (
              <div className="mt-8 border p-8 md:p-10" style={{ borderColor: "var(--reader-accent)" }}
                   data-testid="reader-locked-cta">
                <p className="eyebrow mb-3" style={{ color: "var(--reader-accent)" }}>Locked</p>
                <h3 className="font-display text-3xl mb-3">Continue the journey.</h3>
                <p className="font-editorial italic text-lg mb-6 opacity-80">
                  You've read the free preview. Unlock the full 26-chapter framework — a lifetime companion.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/store" className="btn-gold" data-testid="reader-locked-get-book">
                    <ShoppingBag className="w-4 h-4" /> Get the Book
                  </Link>
                  <button onClick={() => setUnlockOpen(true)} className="btn-outline"
                          style={{ borderColor: "var(--reader-accent)", color: "var(--reader-ink)" }}
                          data-testid="reader-already-bought">
                    <Mail className="w-4 h-4" /> Already bought? Unlock with email
                  </button>
                </div>
              </div>
            )}

            {/* End-of-chapter CTA on chapter 2 free preview */}
            {!locked && chapter.number === 2 && !verifiedEmail && (
              <div className="mt-10 border p-8" style={{ borderColor: "var(--reader-accent)" }}
                   data-testid="reader-ch2-cta">
                <p className="eyebrow mb-3" style={{ color: "var(--reader-accent)" }}>You've reached the end of your free preview</p>
                <h3 className="font-display text-3xl mb-3">There are 24 chapters more.</h3>
                <p className="font-editorial italic opacity-80 mb-5">
                  Bring The Love Genome™ home in print, digital, or bundled with the Companion Workbook.
                </p>
                <Link to="/store" className="btn-gold">
                  Get the Book <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}

            {/* Reflection box */}
            <section className="mt-14 border-t pt-10" style={{ borderColor: "var(--reader-line)" }}>
              <p className="eyebrow mb-3" style={{ color: "var(--reader-accent)" }}>Reflect</p>
              <h4 className="font-display text-2xl mb-2">What is stirring in you?</h4>
              <p className="font-body text-sm opacity-70 mb-4">
                Your reflection is private — stored on this device only. You can export it any time.
              </p>
              <textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                rows={5}
                placeholder="A moment from your life this brought to mind. A question this raised. A verse you'd like to keep close…"
                data-testid="reader-reflection"
                className="w-full p-4 font-body"
                style={{
                  background: "var(--reader-surface)",
                  color: "var(--reader-ink)",
                  border: "1px solid var(--reader-line)",
                  outline: "none",
                  fontSize: `${settings.fontSize - 2}px`,
                }}
              />
            </section>

            {/* Quiz CTA + Streak (for chapters that have quizzes) */}
            {chapter.free && ["preface", "introduction", "ch1", "ch2"].includes(chapterId) && (
              <section className="mt-10 grid md:grid-cols-2 gap-6" data-testid="reader-quiz-section">
                <Link
                  to={`/read/quiz/${chapterId}`}
                  className="border p-6 flex items-center gap-4 hover:opacity-90 transition-opacity"
                  style={{ borderColor: "var(--reader-accent)", background: "var(--reader-surface)" }}
                  data-testid="reader-take-quiz"
                >
                  <div className="w-12 h-12 flex items-center justify-center border shrink-0"
                       style={{ borderColor: "var(--reader-accent)" }}>
                    <Award className="w-5 h-5" style={{ color: "var(--reader-accent)" }} />
                  </div>
                  <div className="flex-1">
                    <p className="eyebrow mb-1" style={{ color: "var(--reader-accent)" }}>Insight Check</p>
                    <p className="font-display text-lg leading-tight">Take the {chapter.label} quiz</p>
                    <p className="font-body text-xs opacity-70 mt-1">Score 100% to earn the badge.</p>
                  </div>
                  <ArrowRight className="w-4 h-4 opacity-60 shrink-0" />
                </Link>
                <StreakCalendar />
              </section>
            )}

            {/* Highlights list */}
            {highlights.length > 0 && (
              <section className="mt-10">
                <div className="flex items-center justify-between mb-3">
                  <p className="eyebrow" style={{ color: "var(--reader-accent)" }}>
                    <Highlighter className="w-4 h-4 inline mr-2 -mt-0.5" />Your highlights
                  </p>
                  <button onClick={exportHighlights} className="text-xs font-body opacity-60 hover:opacity-100"
                          data-testid="reader-export-highlights">
                    <Download className="w-3 h-3 inline mr-1" /> Export
                  </button>
                </div>
                <ul className="space-y-2">
                  {highlights.map((h) => (
                    <li key={h.at} className="flex items-start gap-3 p-3 border" data-testid={`reader-highlight-${h.at}`}
                        style={{ borderColor: "var(--reader-line)", background: "var(--reader-surface)" }}>
                      <span className="flex-1 font-editorial italic text-sm">&ldquo;{h.text}&rdquo;</span>
                      <button onClick={() => removeHighlight(h.text)} title="Remove" className="opacity-50 hover:opacity-100">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Prev / Next */}
            <nav className="mt-14 flex justify-between gap-3 border-t pt-8" style={{ borderColor: "var(--reader-line)" }}>
              <button
                onClick={() => prev && setChapterId(prev.id)}
                disabled={!prev}
                className="flex items-center gap-2 font-body text-sm opacity-80 hover:opacity-100 disabled:opacity-30"
                data-testid="reader-prev"
              >
                <ChevronLeft className="w-4 h-4" /> {prev ? prev.label : ""}
              </button>
              <button
                onClick={() => next && setChapterId(next.id)}
                disabled={!next}
                className="flex items-center gap-2 font-body text-sm opacity-80 hover:opacity-100 disabled:opacity-30"
                data-testid="reader-next"
              >
                {next ? next.label : ""} <ChevronRight className="w-4 h-4" />
              </button>
            </nav>
          </article>
        )}
      </div>

      {/* Unlock modal */}
      {unlockOpen && (        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-6" onClick={() => setUnlockOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="max-w-md w-full p-8"
               style={{ background: "var(--reader-bg)", color: "var(--reader-ink)", border: "1px solid var(--reader-line)" }}
               data-testid="reader-unlock-modal">
            <p className="eyebrow mb-3" style={{ color: "var(--reader-accent)" }}>Unlock your reader</p>
            <h3 className="font-display text-3xl mb-2">Enter the email you used at checkout.</h3>
            <p className="font-body text-sm opacity-70 mb-6">
              We'll email you a magic link that unlocks the full reader on any device you open it from.
            </p>
            <form onSubmit={requestUnlock}>
              <input
                type="email"
                required
                value={unlockEmail}
                onChange={(e) => setUnlockEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="reader-unlock-email"
                className="w-full p-3 mb-4 font-body"
                style={{ background: "var(--reader-surface)", color: "var(--reader-ink)",
                         border: "1px solid var(--reader-line)", outline: "none" }}
              />
              <div className="flex gap-3">
                <button type="submit" disabled={sending} className="btn-emerald flex-1 justify-center disabled:opacity-60"
                        data-testid="reader-unlock-submit">
                  {sending ? "Sending…" : "Send unlock link"}
                </button>
                <button type="button" onClick={() => setUnlockOpen(false)} className="btn-outline"
                        style={{ borderColor: "var(--reader-line)", color: "var(--reader-ink)" }}>
                  Cancel
                </button>
              </div>
            </form>
            <p className="text-[11px] opacity-50 mt-5 font-body">
              Don't have the book yet? <Link to="/store" className="underline">Get it in the store</Link>.
            </p>
          </div>
        </div>
      )}

      {/* Reader Companion — Claude Haiku 4.5 chat, grounded on the current chapter */}
      {chapter && !locked && (
        <ReaderCompanion chapterId={chapterId} chapterLabel={chapter.label} />
      )}
    </div>
  );
};

export default Reader;
